/**
 * Der Inhalt des Detail-Panels „Sicherung S-n" (Zeilenklick in der Sicherungstabelle, Rahmen:
 * `SicherungDetailDialog.tsx`). Er traegt, was es sonst nirgends auf der Zahlungsseite gibt:
 * Datum/Bank/Kurs/Betrag bearbeiten, Beleg hoch-/herunterladen/entfernen und die Bindung an
 * Bestellungen. Herkunft: `fxLedgerEntry(deal, index, bindTargets)`, `index.html:13457-13502`
 * (die fruehere Liste „Kurssicherungen · nachvollziehbare Liste" ist entfallen, Loeschen steht in der Tabelle).
 *
 * Woertlich uebernommene Eigenheiten des Bestands (gemeldet, nicht repariert):
 *   - `deal.oldBalance ?? ''` (Alter Kontostand/Kurs) gegen `deal.usd || ''` (Betrag/Kurs): zwei Leerwert-Regeln.
 *   - `Number(deal.newBalance ?? deal.guthabenNeu) || werte.newBalance` und
 *     `Number(deal.mixedRate) || werte.mixedRate || Number(deal.rate) || 0`: ein gespeicherter 0-Wert wird ersetzt.
 *   - Zuordnungen auf Split-Quellen (`mergedInto`) sind ausgeblendet, `dealAllocated`/`dealFree` zaehlen sie mit.
 *   - Zuordnung auf geloeschte Bestellung: Knopf heisst „Bestellung", Tieflink traegt die rohe Kennung.
 *
 * Kontotyp "Nur lesen" (Task 6 des Plans „Kontotyp Nur lesen", Neubau): die Karte schreibt an vier Stellen -
 * die sechs Felder, Beleg hochladen/entfernen, Bindung loesen und neu binden -, und der Server lehnt alle fuer
 * ein Konto mit nur `view_*`-Rechten ab (`state/nurLeser.ts#istNurLeser(rechte)`). Die Eingabefelder bleiben
 * sichtbar und sind gesperrt, mit Titel und Optik der Sperre im Drawer (`NUR_LESEN_TITEL`, `.feld-gesperrt`);
 * die reinen Aktionen (Beleg hochladen/entfernen, Bindung loesen, das Formular zum Binden) entfallen. Lesen
 * bleibt: der Beleg laesst sich weiter oeffnen, die bestehenden Bindungen bleiben sichtbar. Alle anderen
 * Kontotypen sehen die Karte unveraendert, auch `payment_manager` (hat `manage_fx`).
 *
 * `bindFx` liest Auswahl und Betrag im Bestand beim Klick aus dem DOM; hier zwei Referenzen auf dieselben
 * (ungesteuerten) Felder. `fileUrl(pfad)` (index.html:5330) ist nicht portiert: der Beleg-Verweis entsteht
 * ueber den optionalen Prop `belegUrl`, ohne ihn steht dort der rohe Pfad.
 */
import { fmtUsd } from "../../lib/format.ts";
import {
  dealAllocated,
  dealFree,
  fmtFxDecimal,
  fxCalculatorValues,
  type FxDeal,
} from "../../lib/fx.ts";
import { kennungVon } from "../../lib/bestellkennung.ts";
import type { FxAccount } from "../../api/kurssicherung.ts";
import type { Order } from "../../lib/types.ts";
import { istNurLeser } from "../../state/nurLeser.ts";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { NUR_LESEN_TITEL } from "../Drawer/drawerRechte.ts";
import { Ikone } from "../Ikone.tsx";
import { istEinfacherKlick, Verweis } from "../Verweis.tsx";
import { bestellungNach, type FxBeleg } from "./ZahlungsSeite.tsx";
import { kontoAnzeigenamen } from "./fxAnzeige.ts";
import { useRef } from "react";
import { useDateiDownloadMitStapel } from "../../api/useDateiDownload.ts";
import { BestellungSplitMarke } from "../SplitMarke.tsx";
import { uploadStelle, useUploadStelle, useUploadStellen, type UploadStellen } from "../App/useUploadStellen.ts";
import { UploadAnsage, UploadFortschritt } from "../Upload/UploadFortschritt.tsx";

/** `esc(v)` (index.html:4674) ohne den HTML-Anteil - den erledigt React. */
const text = (wert: unknown): string => String(wert ?? "");

/**
 * Fixrunde 2 zum 401-Download-Bug (Prüfer-Befund 1, Asana 1218211471530480, 07.09.2026): Wortlaut
 * fuer einen gescheiterten Beleg-Download - siehe Kopfkommentar
 * `DokumenteTab.tsx#herunterladen` fuer die Root-Cause (rohes `<a href>` kann den
 * Authorization-Header nicht mitschicken, 401).
 */
const BELEG_DOWNLOAD_FEHLGESCHLAGEN_MELDUNG = "Der Beleg konnte nicht heruntergeladen werden.";

/** Ein Eintrag aus `deal.allocations[]`. */
interface Zuordnung {
  orderId?: unknown;
  usd?: unknown;
}

export interface SicherungDetailProps {
  deal: FxDeal;
  /** Index in der vollen Sicherungsliste: die Schreib-Rueckrufe der App adressieren darueber. */
  index: number;
  /**
   * Die UNGEFILTERTE Bestellliste (Befund W3, Pruefrunde 2). Sie dient der Namensaufloesung BEREITS
   * bestehender Zuordnungen (`bestellungNach`, fuer Bestellnummer/Kennung/Tieflink und den
   * `mergedInto`-Ausschluss). Seit dem Wegfall der Kopffilter auf der Zahlungsseite ist auch
   * `bindeZiele` ungefiltert.
   */
  bestellungen: readonly Order[];
  /**
   * `bindTargets` aus `renderPayPage` (13515): die aktiven USD-Bestellungen, denen eine NEUE Zuordnung
   * entstehen darf. UNGEFILTERT - die Zahlungsseite wendet Laender-/Transportart-Filter der Kopfzeile
   * nicht an (siehe `ZahlungsSeite.tsx`).
   */
  bindeZiele: readonly Order[];
  rechte: ReadonlySet<string>;
  onOeffnen: (bestellungId: string) => void;
  onSicherungAendern?: (index: number, schluessel: string, wert: string) => void;
  /**
   * Die USD-Konten (Kontenstamm) fuer die Bankauswahl; ohne sie ist die Bank nur eine Anzeige. Angeboten werden nur
   * AKTIVE Konten (das stillgelegte Alt-Sammelkonto heisst oft wie eine aktive Bank und taucht nicht auf); die Option
   * traegt die ID, der Name kommt aus `kontoAnzeigenamen`.
   */
  konten?: readonly FxAccount[];
  /**
   * Das WIRKSAME Konto je Sicherung-ID (`overview.deals`): bei Alt-Sicherungen geht die beim Cutover
   * bestaetigte Zuordnung dem rohen `accountId` aus GET /api/fx-deals vor.
   */
  kontoNachDeal?: ReadonlyMap<string, string>;
  onBelegHochladen?: (index: number) => void;
  onBelegEntfernen?: (index: number) => void;
  onBinden?: (index: number, bestellungId: string, usd: number) => void;
  onLoesen?: (index: number, zuordnungIndex: number) => void;
  onHinweis?: (text: string) => void;
  /** Ersatz fuer `fileUrl(pfad)` - siehe Kopfkommentar. */
  belegUrl?: (pfad: unknown) => string;
  /**
   * Der Upload-Store der App (Upload-Sperren, Spec 2026-10-20): welche Sicherung gerade einen Beleg sendet und mit
   * welcher Fortschrittszeile. Ohne durchgereichten Store ist der Knopf nie gesperrt: den Upload startet `App.tsx`, und
   * der schreibt nur in den eigenen Store - das Verhalten vor den Upload-Sperren.
   */
  uploadStellen?: UploadStellen;
}

/** Sperre eines Eingabefelds fuer den Kontotyp "Nur lesen": gesperrt, mit Titel und Optik der Drawer-Sperre. */
const NUR_LESEN_FELD = { disabled: true, title: NUR_LESEN_TITEL, className: "feld-gesperrt" } as const;

/** Das wirksame Konto einer Sicherung: Cutover-Zuordnung (aus der Uebersicht) vor dem Konto am Deal. */
function wirksamesKonto(deal: FxDeal, kontoNachDeal: ReadonlyMap<string, string> | undefined): string {
  return kontoNachDeal?.get(text(deal.id)) ?? text(deal.accountId);
}

/**
 * Die Bankauswahl einer Sicherung. Angeboten werden nur die aktiven Konten. Gesperrt, sobald die Sicherung in einen Kontostand
 * uebernommen ist (`snapshotId`). Bei Alt-Sicherungen mit Cutover-Zuordnung haengt der Server eine
 * Korrektur-Zuordnung an.
 */
function BankAuswahl({ deal, konten, kontoId, onWaehlen, nurLeser }: {
  deal: FxDeal;
  konten: readonly FxAccount[];
  kontoId: string;
  onWaehlen: (kontoId: string) => void;
  nurLeser: boolean;
}) {
  const uebernommen = Boolean(deal.snapshotId);
  const sperrgrund = uebernommen
    ? "Die Sicherung ist bereits übernommen – das Konto kann nicht mehr geändert werden."
    : undefined;
  const namen = kontoAnzeigenamen(konten);
  // Nur AKTIVE Konten stehen zur Wahl. Zeigt die Sicherung noch auf ein stillgelegtes Konto (altes Sammelkonto), steht
  // stattdessen der Banktext der Sicherung da - kein zweites "Sparkasse" in der Liste.
  const aktiv = konten.filter((konto) => konto.isActive);
  // Steht die Sicherung noch auf einem stillgelegten Konto (altes Sammelkonto, in der Praxis: alle Alt-Sicherungen ohne Cutover-
  // Zuordnung), gilt ihr Banktext: ein EINDEUTIG so benanntes aktives Konto ist das wirksame (Bankname -> aktives Konto).
  const nachBank = aktiv.filter((konto) => konto.name === text(deal.bank));
  const wert = aktiv.some((konto) => konto.id === kontoId) ? kontoId : nachBank.length === 1 ? nachBank[0].id : kontoId;
  const bekannt = aktiv.some((konto) => konto.id === wert);
  return (
    // Ein strukturell gesperrtes Feld behaelt seinen treffenderen Titel (wie im Drawer): "bereits uebernommen" gilt
    // fuer jedes Konto, NUR_LESEN_TITEL nur dort, wo sonst nichts den Grund nennt.
    <select
      aria-label="Bank"
      className={nurLeser ? "feld-gesperrt" : undefined}
      disabled={sperrgrund !== undefined || nurLeser}
      title={sperrgrund ?? (nurLeser ? NUR_LESEN_TITEL : undefined)}
      value={wert}
      onChange={(ereignis) => onWaehlen(ereignis.target.value)}
    >
      {!bekannt && <option value={kontoId}>{text(deal.bank) || "Bank wählen …"}</option>}
      {konten
        .filter((konto) => konto.isActive)
        .map((konto) => (
          <option key={konto.id} value={konto.id}>{namen.get(konto.id) ?? konto.name}</option>
        ))}
    </select>
  );
}

export function SicherungDetail({
  deal,
  index,
  bestellungen,
  bindeZiele,
  rechte,
  onOeffnen,
  onSicherungAendern,
  konten,
  kontoNachDeal,
  onBelegHochladen,
  onBelegEntfernen,
  onBinden,
  onLoesen,
  onHinweis,
  belegUrl,
  uploadStellen,
}: SicherungDetailProps) {
  const { ansicht } = useAnsicht();
  // Kontotyp "Nur lesen": siehe Kopfkommentar.
  const nurLeser = istNurLeser(rechte);
  const feldSperre = nurLeser ? NUR_LESEN_FELD : {};
  // Upload-Sperren, Task 7: Sperre und Fortschrittszeile des Belegs dieser Sicherung. Der Schluessel ist derselbe wie in
  // `App.tsx#zahlungenBelegHochladen` (`String(deal.id ?? "")`): `text(deal.id)` bildet dieselbe Zeichenkette.
  const lokaleStellen = useUploadStellen();
  const { gesperrt: belegBusy, anzeige: belegAnzeige } = useUploadStelle(
    uploadStellen ?? lokaleStellen,
    uploadStelle.kurssicherung(text(deal.id)),
  );
  const auswahl = useRef<HTMLSelectElement>(null);
  const betrag = useRef<HTMLInputElement>(null);
  // Kein eigener Fehlerzustand: die Zeile am Ende des Detail-Panels entfaellt (Fehleranzeige Paket 1, Task 9,
  // Ruling R-T8c, Spec R2). Ein gescheiterter Beleg-Download geht an den Fehlerstapel.
  const { herunterladen: herunterladenDatei } = useDateiDownloadMitStapel(
    BELEG_DOWNLOAD_FEHLGESCHLAGEN_MELDUNG,
  );

  // index.html:13458-13466
  const werte = fxCalculatorValues({
    oldBalance: deal.oldBalance || 0,
    oldRate: deal.oldRate || 0,
    newUsd: deal.usd || 0,
    newRate: deal.rate || 0,
  });
  const newBalance = Number(deal.newBalance ?? deal.guthabenNeu) || werte.newBalance;
  const mixedRate = Number(deal.mixedRate) || werte.mixedRate || Number(deal.rate) || 0;
  // isSplitStatusOrder-Ausschluss entfallen (Statusbereinigung) - eine leergezogene
  // Sammellieferungs-Quelle traegt seither `mergedInto`; NICHT `!order.archived`, das traefe jede
  // archivierte Bestellung und liesse eine Zuordnung mit noch gebundenem USD-Betrag spurlos
  // verschwinden ("Lösen" waere dann fuer sie nicht mehr erreichbar).
  const sichtbareZuordnungen = ((deal.allocations || []) as Zuordnung[])
    .map((allocation, allocationIndex) => ({ allocation, allocationIndex }))
    .filter(({ allocation }) => !bestellungNach(bestellungen, allocation.orderId)?.mergedInto);
  const beleg = deal.doc as FxBeleg | null | undefined;
  const url = belegUrl ?? ((pfad: unknown) => text(pfad));

  /**
   * Fixrunde 2 zum 401-Download-Bug (Prüfer-Befund 1, 07.09.2026): DIE ROOT CAUSE war ein rohes
   * `<a href={url(beleg.path)} target="_blank">` auf den Kurssicherungsbeleg - eine
   * Browser-Navigation OHNE JS, die den `Authorization: Bearer <token>`-Header NIE mitschicken
   * kann. `beleg.path` kommt vom Backend als `/api/files/<id>`
   * (`backend/src/domaene/kurssicherung.ts`), derselben Bearer-only-Route wie beim Hauptfix
   * (Commit `1e384b97`) - das Backend liest den Token ausschliesslich aus diesem Header
   * (`backend/src/http/middleware/authenticate.ts`). Derselbe authentifizierte Blob-Weg wie
   * `DsvBelege.tsx` (dortiger Kopfkommentar): `api/basis.ts#ladeUndSpeichereDatei` holt die Datei
   * per `fetch` MIT Header, baut daraus eine Object-URL und stoesst den Download darueber an.
   *
   * Der Mechanismus dahinter (Aufruf von `ladeUndSpeichereDatei`, Fehlerweitergabe) kommt seit
   * Auftrag "SicherungsListe.tsx (heute SicherungDetail.tsx) auf useDateiDownload() umstellen" (08.09.2026) aus dem
   * gemeinsamen Hook (`api/useDateiDownload.ts`, Vorbild `DsvBelege.tsx`) statt aus einer lokalen Kopie;
   * seit Fehleranzeige Paket 1, Task 9 ist es dessen Stapel-Fassung `useDateiDownloadMitStapel`. `url()` steht bewusst WEITERHIN VOR dem Hook-Aufruf: sie
   * beruecksichtigt den `belegUrl`-Prop (siehe Kopfkommentar oben), was der Hook selbst nicht
   * kann - deshalb bleibt dieser duenne Wrapper, der `url(pfad)` erst aufloest und das Ergebnis
   * dann an `herunterladenDatei` reicht.
   */
  const herunterladen = (pfad: unknown, dateiname: string): Promise<void> =>
    herunterladenDatei(url(pfad), dateiname);

  /** `bindFx(i)`, 13634: Auswahl und Betrag kommen aus den beiden Feldern der Zeile. */
  const binden = (): void => {
    onBinden?.(index, auswahl.current?.value ?? "", Number(betrag.current?.value) || 0);
  };

  return (
    <div className="fx-sicherung-detail">
      <div className="fx-ledger-body">
        <div className="fx-ledger-fields">
          <div className="fx-ledger-field">
            <label>Datum</label>
            <input
              type="date"
              value={text(deal.date)}
              onChange={(ereignis) => onSicherungAendern?.(index, "date", ereignis.target.value)}
              {...feldSperre}
            />
          </div>
          <div className="fx-ledger-field">
            <label>Bank</label>
            {konten ? (
              <BankAuswahl
                deal={deal}
                konten={konten}
                kontoId={wirksamesKonto(deal, kontoNachDeal)}
                onWaehlen={(kontoId) => onSicherungAendern?.(index, "accountId", kontoId)}
                nurLeser={nurLeser}
              />
            ) : (
              // Kontenliste nicht geladen (fehlende Berechtigung, Ladefehler): die Bank nur anzeigen, nicht frei tippen.
              // Das ist die Lage eines Nur-Lesers (die App laedt die Konten nur mit `manage_fx`): sein Bank-Feld
              // traegt dieselbe Sperre wie die uebrigen Felder.
              <input value={text(deal.bank)} readOnly {...feldSperre} />
            )}
          </div>
          {/* Eigenheit 1: `?? ''` hier, `|| ''` zwei Felder weiter unten. */}
          <div className="fx-ledger-field">
            <label>Alter Kontostand</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={text(deal.oldBalance ?? "")}
              onChange={(ereignis) =>
                onSicherungAendern?.(index, "oldBalance", ereignis.target.value)
              }
              {...feldSperre}
            />
          </div>
          <div className="fx-ledger-field">
            <label>Alter Dollarkurs</label>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={text(deal.oldRate ?? "")}
              onChange={(ereignis) => onSicherungAendern?.(index, "oldRate", ereignis.target.value)}
              {...feldSperre}
            />
          </div>
          <div className="fx-ledger-field">
            <label>Neu erhaltener Dollarbetrag</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={text(deal.usd || "")}
              onChange={(ereignis) => onSicherungAendern?.(index, "usd", ereignis.target.value)}
              {...feldSperre}
            />
          </div>
          <div className="fx-ledger-field">
            <label>Neuer Dollarkurs</label>
            <input
              type="number"
              min="0"
              step="0.0001"
              value={text(deal.rate || "")}
              onChange={(ereignis) => onSicherungAendern?.(index, "rate", ereignis.target.value)}
              {...feldSperre}
            />
          </div>
          <div className="fx-ledger-field">
            <label>Guthaben neu</label>
            <input readOnly value={fmtFxDecimal(newBalance, 2, true)} />
          </div>
          <div className="fx-ledger-field">
            <label>Mischkurs USD je EUR</label>
            <input readOnly value={fmtFxDecimal(mixedRate, 6, true)} />
          </div>
        </div>
        <div className="fx-ledger-actions">
          <div>
            {beleg ? (
              <>
                <a
                  className="fx-doc-link icon-label"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    void herunterladen(beleg.path, text(beleg.name));
                  }}
                >
                  <Ikone name="receipt-text" />
                  {text(beleg.name)}
                </a>{" "}
                {nurLeser ? null : (
                  <button
                    type="button"
                    className="row-del"
                    title="Beleg entfernen"
                    onClick={() => onBelegEntfernen?.(index)}
                  >
                    <Ikone name="trash-2" />
                  </button>
                )}
              </>
            ) : nurLeser ? null : (
              <button type="button" className="btn-ghost" disabled={belegBusy} onClick={() => onBelegHochladen?.(index)}>
                <Ikone name="upload" />
                Beleg hochladen
              </button>
            )}
            {/* Die Ansage-Region steht dauerhaft hier (in beiden Zweigen), die Zeile nur waehrend des Uploads. */}
            <UploadAnsage anzeigen={belegAnzeige ? [{ schluessel: uploadStelle.kurssicherung(text(deal.id)), anzeige: belegAnzeige }] : []} />
            {/* In beiden Zweigen: haengt waehrend des Uploads jemand anderes einen Beleg an, ersetzt der laufende ihn gleich. */}
            {belegAnzeige ? <UploadFortschritt {...belegAnzeige} /> : null}
          </div>
        </div>
        <details className="fx-detail" open={sichtbareZuordnungen.length > 0}>
          <summary>{`Bindungen · ${fmtUsd(dealAllocated(deal))} gebunden · ${fmtUsd(dealFree(deal))} frei`}</summary>
          <div className="fx-allocs">
            {sichtbareZuordnungen.map(({ allocation, allocationIndex }) => {
              // `bestellungNach` liefert `undefined` bei einer Zuordnung auf eine geloeschte
              // Bestellung (Woertlichkeit 7 im Kopfkommentar) - `kennungVon` braucht dafuer einen
              // echten Datensatz, den es dann nicht gibt. Der Rueckfall ist dieselbe rohe Kennung,
              // die `onOeffnen` schon immer bekam.
              const orderId = text(allocation.orderId);
              const bestellung = bestellungNach(bestellungen, allocation.orderId);
              const kennung = bestellung ? kennungVon(bestellung, orderId) : orderId;
              return (
                <span className="fx-alloc-chip" key={allocationIndex}>
                  <Verweis
                    className="tieflink-anker"
                    replace
                    // Befund 1 der Pruefrunde 3 (Fixrunde 3): `onClick` sitzt auf dem Anker, nicht auf
                    // dem inneren `<button>` - Begruendung an derselben Stelle in Handlungsbedarf.tsx
                    // (dortiger Kopfkommentar am `<Verweis>`).
                    onClick={(ereignis) => { if (istEinfacherKlick(ereignis)) onOeffnen(orderId); }}
                    ziel={{ ...ansicht, bestellung: { kennung, tab: "main", aufgabe: null, dokument: null } }}
                  >
                    {/* `<a><button>` bleibt bewusst bestehen - offener Punkt, siehe
                        Handlungsbedarf.tsx (dortiger Kommentar am Knopf) und Fixbericht Aufgabe 5,
                        Fixrunde 3. Kein eigenes `onClick` mehr am Knopf - `tabIndex={-1}`
                        NACHGETRAGEN (Endabnahme-Nachbesserung, Punkt 3), Begruendung ebenfalls dort. */}
                    <button className="link-btn" tabIndex={-1}>
                      {text(bestellung?.orderNo || "Bestellung")}
                    </button>
                  </Verweis>
                  <BestellungSplitMarke order={bestellung} />
                  {` · ${fmtUsd(Number(allocation.usd) || 0)} `}
                  {nurLeser ? null : (
                    <button
                      type="button"
                      className="row-del"
                      title="Bindung lösen"
                      onClick={() => onLoesen?.(index, allocationIndex)}
                    >
                      <Ikone name="unlink" />
                    </button>
                  )}
                </span>
              );
            })}
            {bindeZiele.length && !nurLeser ? (
              <span className="fx-alloc-add">
                <select id={`fxSel-${text(deal.id)}`} ref={auswahl}>
                  {bindeZiele.map((order) => (
                    <option value={text(order.id)} key={text(order.id)}>
                      {text(order.orderNo || order.name || "Bestellung")}
                    </option>
                  ))}
                </select>
                <input
                  id={`fxAmt-${text(deal.id)}`}
                  className="price"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={Math.round(dealFree(deal) * 100) / 100}
                  ref={betrag}
                />
                <button type="button" className="btn-ghost" onClick={binden}>
                  <Ikone name="link" />
                  Binden
                </button>
              </span>
            ) : (
              ""
            )}
          </div>
        </details>
      </div>
    </div>
  );
}
